/**
 * Forecast Statistics Library
 *
 * Statistical methods for sales forecast calibration (US-FORECAST-CALIBRATION Step 3).
 * Provides exponential smoothing, log transforms, Bayesian bounds,
 * seasonal adjustments, and accuracy metrics.
 */

// =============================================================================
// CONFIGURATION
// =============================================================================

export const CONFIG = {
  EXPONENTIAL_SMOOTHING: {
    ALPHA: 0.3,      // Level smoothing factor (0-1, higher = more weight on recent)
    BETA: 0.1,       // Trend smoothing factor (0-1, higher = faster trend response)
    PHI: 0.95        // Trend dampening factor (0-1, lower = more dampening)
  },
  SEASONAL_FACTORS: {
    // Monthly adjustment multipliers (1.0 = no adjustment)
    // Based on typical mushroom demand patterns in UK market
    1: 0.85,   // January - post-Christmas low
    2: 0.90,   // February - slow recovery
    3: 0.95,   // March - spring increase
    4: 1.00,   // April - baseline
    5: 1.05,   // May - rising
    6: 1.10,   // June - peak summer restaurant demand
    7: 1.15,   // July - peak
    8: 1.10,   // August - holiday dip starts
    9: 1.05,   // September - back to business
    10: 1.00,  // October - baseline
    11: 0.95,  // November - pre-Christmas variable
    12: 0.90   // December - Christmas disruption
  }
}

// =============================================================================
// EXPONENTIAL SMOOTHING
// =============================================================================

/**
 * Triple exponential smoothing with trend dampening (Holt-Winters method).
 * Predicts next value based on level, trend, and dampening factors.
 *
 * @param {number[]} data - Historical time series data (oldest first)
 * @param {Object} config - Smoothing configuration
 * @param {number} [config.alpha=0.3] - Level smoothing (0-1)
 * @param {number} [config.beta=0.1] - Trend smoothing (0-1)
 * @param {number} [config.phi=0.95] - Trend dampening (0-1, where 1 = no dampening)
 * @returns {Object} { level, trend, forecast, series }
 *
 * @example
 * const result = exponentialSmoothing([10, 12, 14, 13, 15], { alpha: 0.3, beta: 0.1, phi: 0.95 })
 * console.log(result.forecast) // Predicted next value
 */
export function exponentialSmoothing(data, config = {}) {
  const {
    alpha = CONFIG.EXPONENTIAL_SMOOTHING.ALPHA,
    beta = CONFIG.EXPONENTIAL_SMOOTHING.BETA,
    phi = CONFIG.EXPONENTIAL_SMOOTHING.PHI
  } = config

  if (!data || data.length === 0) {
    return { level: 0, trend: 0, forecast: 0, series: [] }
  }

  if (data.length === 1) {
    return { level: data[0], trend: 0, forecast: data[0], series: [data[0]] }
  }

  // Initialise level and trend
  let level = data[0]
  let trend = data[1] - data[0]
  const series = [level]

  // Process each data point
  for (let i = 1; i < data.length; i++) {
    const prevLevel = level
    const prevTrend = trend

    // Update level
    level = alpha * data[i] + (1 - alpha) * (prevLevel + phi * prevTrend)

    // Update trend
    trend = beta * (level - prevLevel) + (1 - beta) * phi * prevTrend

    series.push(level)
  }

  // Forecast next value with dampened trend
  const forecast = level + phi * trend

  return {
    level: Math.round(level * 1000) / 1000,
    trend: Math.round(trend * 1000) / 1000,
    forecast: Math.round(forecast * 1000) / 1000,
    series: series.map(v => Math.round(v * 1000) / 1000)
  }
}

// =============================================================================
// LOG TRANSFORMS
// =============================================================================

/**
 * Apply log transform to a value for variance stabilisation.
 * Uses log(1 + x) to handle zero values gracefully.
 *
 * @param {number} value - Value to transform
 * @returns {number} Log-transformed value
 *
 * @example
 * logTransform(100) // 4.615...
 * logTransform(0)   // 0
 */
export function logTransform(value) {
  if (value <= 0) return 0
  return Math.log(1 + value)
}

/**
 * Inverse log transform to convert back from log space.
 *
 * @param {number} value - Log-transformed value
 * @returns {number} Original scale value
 *
 * @example
 * inverseLogTransform(logTransform(100)) // ~100
 */
export function inverseLogTransform(value) {
  return Math.exp(value) - 1
}

// =============================================================================
// BAYESIAN BOUNDS
// =============================================================================

/**
 * Calculate Bayesian-inspired bounds and constrain a prediction within them.
 * Uses a weighted prior (historical mean) to pull extreme predictions toward reality.
 *
 * @param {number} rawPrediction - The unconstrained prediction value
 * @param {number} mean - Historical mean (prior expectation)
 * @param {number} stddev - Historical standard deviation
 * @param {number} [numSigma=2] - Number of standard deviations for bounds
 * @returns {Object} { bounded, lowerBound, upperBound, wasConstrained }
 *
 * @example
 * calculateBayesianBounds(100, 20, 5, 2)
 * // { bounded: 30, lowerBound: 10, upperBound: 30, wasConstrained: true }
 */
export function calculateBayesianBounds(rawPrediction, mean, stddev, numSigma = 2) {
  // Handle edge cases
  if (mean <= 0 || stddev < 0) {
    return {
      bounded: rawPrediction,
      lowerBound: 0,
      upperBound: rawPrediction,
      wasConstrained: false
    }
  }

  // Calculate bounds
  const lowerBound = Math.max(0, mean - numSigma * stddev)
  const upperBound = mean + numSigma * stddev

  // Constrain prediction
  const bounded = Math.min(Math.max(rawPrediction, lowerBound), upperBound)
  const wasConstrained = bounded !== rawPrediction

  return {
    bounded: Math.round(bounded * 1000) / 1000,
    lowerBound: Math.round(lowerBound * 1000) / 1000,
    upperBound: Math.round(upperBound * 1000) / 1000,
    wasConstrained
  }
}

// =============================================================================
// SEASONAL ADJUSTMENT
// =============================================================================

/**
 * Get seasonal adjustment factor for a given month.
 * Uses configured monthly multipliers to adjust predictions.
 *
 * @param {number} month - Month number (1-12)
 * @returns {number} Seasonal multiplier (e.g., 0.85 for January, 1.15 for July)
 *
 * @example
 * getSeasonalFactor(7)  // 1.15 (July peak)
 * getSeasonalFactor(1)  // 0.85 (January low)
 */
export function getSeasonalFactor(month) {
  if (month < 1 || month > 12) {
    console.warn(`[forecastStatistics] Invalid month ${month}, returning 1.0`)
    return 1.0
  }
  return CONFIG.SEASONAL_FACTORS[month] || 1.0
}

// =============================================================================
// ACCURACY METRICS
// =============================================================================

/**
 * Calculate Mean Absolute Percentage Error (MAPE).
 * MAPE measures average prediction accuracy as a percentage.
 * Lower is better: <10% excellent, 10-20% good, 20-50% reasonable, >50% poor.
 *
 * @param {number[]} predictions - Array of predicted values
 * @param {number[]} actuals - Array of actual values (same length)
 * @returns {number} MAPE as decimal (0.15 = 15% error)
 *
 * @example
 * calculateMAPE([100, 110, 90], [95, 100, 100])
 * // Returns ~0.10 (10% average error)
 */
export function calculateMAPE(predictions, actuals) {
  if (!predictions || !actuals || predictions.length !== actuals.length) {
    console.warn('[forecastStatistics] MAPE: predictions and actuals must be equal length arrays')
    return null
  }

  if (predictions.length === 0) {
    return null
  }

  let totalPercentError = 0
  let validCount = 0

  for (let i = 0; i < predictions.length; i++) {
    const actual = actuals[i]
    const predicted = predictions[i]

    // Skip zero actuals (would cause division by zero)
    if (actual === 0) continue

    const percentError = Math.abs((actual - predicted) / actual)
    totalPercentError += percentError
    validCount++
  }

  if (validCount === 0) {
    return null
  }

  const mape = totalPercentError / validCount
  return Math.round(mape * 10000) / 10000  // 4 decimal places
}

/**
 * Calculate systematic bias in predictions.
 * Positive bias = overprediction, Negative bias = underprediction.
 *
 * @param {number[]} predictions - Array of predicted values
 * @param {number[]} actuals - Array of actual values (same length)
 * @returns {number} Bias as decimal (0.15 = 15% overprediction on average)
 *
 * @example
 * calculateBias([110, 120, 100], [100, 100, 100])
 * // Returns 0.10 (10% systematic overprediction)
 */
export function calculateBias(predictions, actuals) {
  if (!predictions || !actuals || predictions.length !== actuals.length) {
    console.warn('[forecastStatistics] Bias: predictions and actuals must be equal length arrays')
    return null
  }

  if (predictions.length === 0) {
    return null
  }

  let totalBias = 0
  let validCount = 0

  for (let i = 0; i < predictions.length; i++) {
    const actual = actuals[i]
    const predicted = predictions[i]

    // Skip zero actuals
    if (actual === 0) continue

    const bias = (predicted - actual) / actual
    totalBias += bias
    validCount++
  }

  if (validCount === 0) {
    return null
  }

  const avgBias = totalBias / validCount
  return Math.round(avgBias * 10000) / 10000  // 4 decimal places
}

// =============================================================================
// EXPORTS SUMMARY
// =============================================================================

/**
 * Module exports:
 * - CONFIG: Configuration constants for smoothing and seasonal factors
 * - exponentialSmoothing: Holt-Winters triple smoothing with trend dampening
 * - logTransform / inverseLogTransform: Variance stabilisation transforms
 * - calculateBayesianBounds: Constrain predictions to statistical bounds
 * - getSeasonalFactor: Monthly demand adjustment multipliers
 * - calculateMAPE: Mean Absolute Percentage Error
 * - calculateBias: Systematic over/under prediction measurement
 */
